import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Grid2 as Grid,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  useAddGraduationClassMutation,
  useUpdateGraduationClassMutation,
} from "graphql/__generated__/hooks";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { GraduationClass } from "types/graduationClass";
import { getAcademicYearDates } from "utils/Datetime";
import { useNotifications } from "hooks/useNotifications";
import { Messages, NotificationSeverity } from "helpers/constants";
import dayjs from "dayjs";
import { useClinicText } from "hooks/useClinicText";
import { Organization } from "types/organization";

type GraduationClassStepProps = {
  organizationId?: string;
  academicYearId?: string;
  programId?: string;
  graduationClass?: GraduationClass;
  organization?: Organization;
  onGraduationClassCreated: (graduationClassId: string) => void;
  onBack: () => void;
  onNext: () => void;
};

export const GraduationClassStep: FC<GraduationClassStepProps> = ({
  academicYearId,
  programId,
  graduationClass,
  organization,
  onGraduationClassCreated,
  onBack,
  onNext,
}) => {
  const [loading, setLoading] = useState(false);
  const [addGraduationClass] = useAddGraduationClassMutation();
  const [updateGraduationClass] = useUpdateGraduationClassMutation();
  const { start_date } = getAcademicYearDates();
  const { notify } = useNotifications();
  const { textGraduationClass } = useClinicText(false, organization);

  const initialValues = {
    name: "",
    start_date: start_date,
  };

  const validationSchema = () =>
    Yup.object().shape({
      name: Yup.string().required("Class Name is required"),
      start_date: Yup.string().required("Start Date is required"),
    });

  const handleCreateGraduationClass = async (reqValues: GraduationClass) => {
    setLoading(true);
    try {
      const result = await addGraduationClass({
        variables: {
          name: reqValues.name,
          start_date: reqValues.start_date,
          program_id: programId,
          current_academic_year_id: academicYearId,
        },
      });

      const graduationClassId =
        result.data?.insert_graduation_class?.returning[0]?.graduation_class_id;

      if (graduationClassId) {
        onGraduationClassCreated(graduationClassId);
        notify({
          message: Messages.successGraduationClassCreated,
          severity: NotificationSeverity.success,
        });
      }
    } catch (error) {
      notify({
        message: `Error creating ${textGraduationClass}`,
        severity: NotificationSeverity.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGraduationClass = async (reqValues: GraduationClass) => {
    setLoading(true);
    try {
      await updateGraduationClass({
        variables: {
          id: graduationClass?.graduation_class_id,
          graduationClass: {
            name: reqValues.name,
            start_date: reqValues.start_date,
            current_academic_year_id: academicYearId,
          },
        },
      });
      onNext();
      notify({
        message: Messages.successGraduationClassUpdated,
        severity: NotificationSeverity.success,
      });
    } catch (error) {
      notify({
        message: `Error updating ${textGraduationClass}`,
        severity: NotificationSeverity.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const reqValues = {
        name: values.name,
        start_date: values.start_date,
        current_academic_year_id: academicYearId,
      };
      if (graduationClass) {
        await handleUpdateGraduationClass(reqValues as GraduationClass);
      } else {
        await handleCreateGraduationClass(reqValues as GraduationClass);
      }
      onNext();
    },
  });

  useEffect(() => {
    if (graduationClass) {
      formik.setValues({
        name: graduationClass.name,
        start_date: graduationClass.start_date || start_date,
      });
    }
  }, [graduationClass]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 1 }}>
          Create {textGraduationClass}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Set up your {textGraduationClass.toLowerCase()} and manage student
          enrollment
        </Typography>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* Class Name */}
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  onChange={formik.handleChange}
                  fullWidth
                  name="name"
                  label="Class Name *"
                  type="text"
                  id="name"
                  value={formik?.values?.name}
                  placeholder="e.g., Class of 2028 - Doctor of Veterinary Medicine"
                  helperText={`This will be the display name for your ${textGraduationClass}`}
                  size="small"
                  margin="dense"
                />
                <ErrorMessage name="name" />
              </Grid>

              {/* Start Date y Program */}
              <Grid size={{ xs: 12 }}>
                <DatePicker
                  label="Start Date *"
                  value={dayjs(formik?.values?.start_date)}
                  onChange={(newValue) => {
                    formik.setFieldValue(
                      "start_date",
                      newValue?.format("YYYY-MM-DD"),
                    );
                  }}
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      size: "small",
                      margin: "dense",
                    },
                  }}
                />
                <ErrorMessage name="start_date" />
              </Grid>

              <Grid size={12}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="outlined"
                    onClick={onBack}
                    sx={{ minWidth: 100 }}
                  >
                    Back
                  </Button>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={onNext}
                      disabled={loading}
                    >
                      Skip this step and continue
                    </Button>
                    <Button
                      disabled={loading}
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: 150 }}
                    >
                      Next
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </FormikProvider>
      </Box>
    </LocalizationProvider>
  );
};
